import { Icon } from '@iconify/react';
import { val } from 'cheerio/lib/api/attributes';
import classNames from 'classnames';
import { Calendar } from 'primereact/calendar';
import { Chips } from 'primereact/chips';
import { useState } from 'react';

import cn from './Input.module.sass';

type InputProps = {
  title: string;
  titleClass?: string;
  inputClass?: string;
  divClass?: string;
  type?: string;
  name: string;
  value?: any;
  onChange?: any;
  register?: any;
  errors?: any;
  icon?: any;
  autoComplete?: string;
  rows?: number;
  rules?: {
    required?: boolean | string;
    min?: number | object;
    max?: number | object;
    minLength?:
      | number
      | {
          value: number;
          message: string;
        };
    maxLength?:
      | number
      | {
          value: number;
          message: string;
        };
    pattern?:
      | RegExp
      | {
          value: RegExp;
          message: string;
        };
    validate?: (value: string) => boolean | string;
  };
  placeholder?: string;
  disabled?: boolean;
  gradient?: boolean;
};

export function Input({
  title,
  titleClass,
  inputClass,
  divClass,
  type = 'text',
  register,
  icon,
  name,
  value,
  onChange,
  rules = {},
  errors,
  rows,
  placeholder,
  disabled,
  gradient,
  ...rest
}: InputProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  function errorGenerator(errorObj: any) {
    switch (errorObj?.type) {
      case 'required':
        return errorObj?.message || 'Dit veld is verplicht';
      case 'minLength':
        return `Dit veld moet minimaal ${errorObj.message} tekens bevatten`;
      case 'maxLength':
        return `Dit veld mag maximaal ${errorObj.message} tekens bevatten`;
      case 'min':
        return `Dit veld moet minimaal ${errorObj.message} zijn`;
      case 'max':
        return `Dit veld mag maximaal ${errorObj.message} zijn`;
      case 'pattern':
        return errorObj.message;
      case 'validate':
        return errorObj?.message || 'Dit veld is niet geldig';
      default:
        return null;
    }
  }

  return (
    <div className={`${cn.input} ${divClass}`}>
      {title && (
        <label
          className={classNames(
            `${cn.input__title} ${titleClass && titleClass}`,
            errors?.[name] && '!border-rose-600'
          )}
        >
          {title}
          {rules?.required && <span className={cn.required}> *</span>}
        </label>
      )}

      {type !== 'chips' &&
        type !== 'textarea' &&
        type !== 'date' &&
        type !== 'password' &&
        type !== 'inputname' && (
          <input
            type={type}
            className={classNames(
              cn.input__element,
              'input_component',
              inputClass,

              errors?.[name] && '!border-rose-600 focus:!border-primary'
            )}
            name={name}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            icon={icon}
            {...register?.(name, rules)}
            {...rest}
          />
        )}

      {type === 'inputname' && (
        <input
          type={type}
          className={classNames(
            cn.input__element,
            'input_component',
            inputClass,

            errors?.[name] && '!border-rose-600 focus:!border-primary'
          )}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          icon={icon}
          value={value}
          {...register?.(name, rules)}
          {...rest}
        />
      )}

      {type === 'password' && (
        <div className={`${cn.passwordToggle}`}>
          <input
            type={isPasswordVisible ? 'text' : 'password'}
            className={classNames(
              cn.input__element,
              'input_component',
              inputClass,
              errors?.[name] && '!border-rose-600 focus:!border-primary'
            )}
            name={name}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            {...register?.(name, rules)}
            {...rest}
          />
          <button
            type="button"
            className="absolute top-1/2 right-2 transform -translate-y-1/2 cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            <Icon
              icon={
                isPasswordVisible ? 'fa6-regular:eye' : 'gravity-ui:eye-slash'
              }
              className="text-[#999999]"
            />
          </button>
        </div>
      )}

      {type === 'textarea' && (
        <textarea
          className={`${cn.input__element} ${inputClass && inputClass}`}
          name={name}
          onChange={onChange}
          rows={rows}
          disabled={disabled}
          {...register?.(name, rules)}
          {...rest}
        />
      )}

      {type === 'chips' && (
        <Chips
          name={name + 'x'}
          value={value}
          onChange={onChange}
          separator=","
          allowDuplicate={false}
          addOnBlur
          autoComplete="new-keywords"
          disabled={disabled}
        />
      )}

      {type === 'date' && (
        <Calendar
          name={name}
          value={value}
          onChange={onChange}
          dateFormat="dd-mm-yy"
          showIcon
        />
      )}

      <span className={cn.error}>{errorGenerator(errors?.[name])}</span>
    </div>
  );
}
